import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Typography } from 'antd';
import {
  MailOutlined,
  LockOutlined,
  ShopOutlined,
  CarOutlined,
  BarChartOutlined,
  SecurityScanOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import './Login.css';

const { Text } = Typography;

const HIGHLIGHTS = [
  {
    icon: <ShopOutlined />,
    title: 'Store & catalog',
    text: 'Products, inventory, orders and billing in one place.',
  },
  {
    icon: <CarOutlined />,
    title: 'Operations',
    text: 'Providers, delivery and live tracking at a glance.',
  },
  {
    icon: <BarChartOutlined />,
    title: 'Insights',
    text: 'Analytics, revenue and customer data that drive decisions.',
  },
];

const Login = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('credentials');
  const [userId, setUserId] = useState(null);
  const [code, setCode] = useState('');
  const navigate = useNavigate();
  const { login, verify2fa } = useAuthStore();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === '1') {
      message.warning('Your session has expired. Please login again.');
      window.history.replaceState({}, document.title, '/admin/');
    }
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const result = await login(values);
      if (result.requires_2fa) {
        setUserId(result.user_id);
        setStep('verify');
      } else if (result.success) {
        message.success('Login successful!');
        navigate('/');
      } else {
        message.error(result.error || 'Login failed');
      }
    } catch (error) {
      message.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onVerify2fa = async () => {
    if (!code || code.length !== 6) {
      message.warning('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const result = await verify2fa(userId, code);
      if (result.success) {
        message.success('Login successful!');
        navigate('/');
      } else {
        message.error(result.error || 'Verification failed');
      }
    } catch (error) {
      message.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('credentials');
    setCode('');
    setUserId(null);
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-form">
          <div className="auth-logo-link">
            <img
              src="/images/currentcare-logo-mark.png"
              alt="Current Care"
              className="auth-logo"
              onError={(e) => {
                e.target.src =
                  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect width=%22100%22 height=%22100%22 rx=%2220%22 fill=%22%232d5a3d%22/%3E%3Ctext x=%2250%22 y=%2268%22 font-family=%22Arial,sans-serif%22 font-size=%2244%22 font-weight=%22bold%22 fill=%22white%22 text-anchor=%22middle%22%3ECC%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>

          {step === 'credentials' ? (
            <>
              <h1 className="auth-title">Welcome back</h1>
              <p className="auth-sub">Sign in to the Current Care admin dashboard.</p>

              <Form form={form} layout="vertical" size="large" onFinish={onFinish} requiredMark={false}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="you@example.com" />
                </Form.Item>
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[{ required: true, message: 'Please enter your password' }]}
                >
                  <Input.Password prefix={<LockOutlined />} placeholder="Your password" />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  className="auth-submit"
                >
                  Sign in
                </Button>
              </Form>

              <div className="auth-foot">Authorized administrators only.</div>
            </>
          ) : (
            <>
              <h1 className="auth-title">Two-Factor Authentication</h1>
              <p className="auth-sub">Enter the 6-digit code from your authenticator app.</p>

              <div style={{ textAlign: 'center', margin: '32px 0' }}>
                <SecurityScanOutlined style={{ fontSize: 48, color: '#2d5a3d', marginBottom: 16 }} />
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  placeholder="000000"
                  style={{
                    textAlign: 'center',
                    fontSize: 28,
                    letterSpacing: 8,
                    height: 56,
                    borderRadius: 10,
                    maxWidth: 280,
                    margin: '0 auto',
                  }}
                />
              </div>

              <Button
                type="primary"
                block
                size="large"
                loading={loading}
                onClick={onVerify2fa}
                className="auth-submit"
              >
                Verify
              </Button>

              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={handleBack}>
                  Back to login
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-guide">
          <h2>Current Care Admin</h2>
          <p>Run the whole operation from a single dashboard.</p>
          <div className="auth-flow">
            {HIGHLIGHTS.map((h) => (
              <div className="auth-flow-step" key={h.title}>
                <div className="auth-flow-num">{h.icon}</div>
                <div className="auth-flow-body">
                  <b>{h.title}</b>
                  <span>{h.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
